import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Users, Briefcase, Plus, Edit2, Trash2, Eye, EyeOff, GripVertical,
  Loader2, Image, Link as LinkIcon, Save, X, ExternalLink, Star
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const WebsiteContentPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('team');
  const [loading, setLoading] = useState(true);

  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: '',
    role: '',
    bio: '',
    photo_url: '',
    linkedin_url: '',
    email: '',
    is_active: true
  });

  // Portfolio state
  const [caseStudies, setCaseStudies] = useState([]);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [caseForm, setCaseForm] = useState({
    title: '',
    client: '',
    description: '',
    category: '',
    services: [],
    image_url: '',
    gallery: [],
    results: '',
    testimonial: '',
    testimonial_author: '',
    link: '',
    published: true
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamRes, casesRes] = await Promise.all([
        api.get('/website/team'),
        api.get('/website/case-studies')
      ]);
      setTeamMembers(teamRes.data || []);
      setCaseStudies(casesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // ===== TEAM FUNCTIONS =====
  const openTeamModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setMemberForm({
        name: member.name || '',
        role: member.role || '',
        bio: member.bio || '',
        photo_url: member.photo_url || '',
        linkedin_url: member.linkedin_url || '',
        email: member.email || '',
        is_active: member.is_active !== false
      });
    } else {
      setEditingMember(null);
      setMemberForm({
        name: '',
        role: '',
        bio: '',
        photo_url: '',
        linkedin_url: '',
        email: '',
        is_active: true
      });
    }
    setShowTeamModal(true);
  };

  const handleSaveTeamMember = async () => {
    if (!memberForm.name || !memberForm.role) {
      toast.error('Nom et rôle sont obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      if (editingMember) {
        await api.put(`/website/team/${editingMember.id}`, memberForm);
        toast.success('Membre mis à jour');
      } else {
        await api.post('/website/team', memberForm);
        toast.success('Membre ajouté');
      }
      setShowTeamModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!confirm('Supprimer ce membre ?')) return;
    
    try {
      await api.delete(`/website/team/${memberId}`);
      toast.success('Membre supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleMemberVisibility = async (member) => {
    try {
      await api.put(`/website/team/${member.id}`, { is_active: !member.is_active });
      toast.success(member.is_active ? 'Membre masqué' : 'Membre affiché');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // ===== PORTFOLIO FUNCTIONS =====
  const openPortfolioModal = (caseStudy = null) => {
    if (caseStudy) {
      setEditingCase(caseStudy);
      setCaseForm({
        title: caseStudy.title || '',
        client: caseStudy.client || '',
        description: caseStudy.description || '',
        category: caseStudy.category || '',
        services: caseStudy.services || [],
        image_url: caseStudy.image_url || '',
        gallery: caseStudy.gallery || [],
        results: caseStudy.results || '',
        testimonial: caseStudy.testimonial || '',
        testimonial_author: caseStudy.testimonial_author || '',
        link: caseStudy.link || '',
        published: caseStudy.published !== false
      });
    } else {
      setEditingCase(null);
      setCaseForm({
        title: '',
        client: '',
        description: '',
        category: '',
        services: [],
        image_url: '',
        gallery: [],
        results: '',
        testimonial: '',
        testimonial_author: '',
        link: '',
        published: true
      });
    }
    setShowPortfolioModal(true);
  };

  const handleSaveCaseStudy = async () => {
    if (!caseForm.title || !caseForm.client) {
      toast.error('Titre et client sont obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCase) {
        await api.put(`/website/case-studies/${editingCase.id}`, caseForm);
        toast.success('Réalisation mise à jour');
      } else {
        await api.post('/website/case-studies', caseForm);
        toast.success('Réalisation ajoutée');
      }
      setShowPortfolioModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCaseStudy = async (caseId) => {
    if (!confirm('Supprimer cette réalisation ?')) return;
    
    try {
      await api.delete(`/website/case-studies/${caseId}`);
      toast.success('Réalisation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleCasePublished = async (caseStudy) => {
    try {
      await api.put(`/website/case-studies/${caseStudy.id}`, { published: !caseStudy.published });
      toast.success(caseStudy.published ? 'Réalisation masquée' : 'Réalisation publiée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const CATEGORIES = [
    'Site web', 'Application mobile', 'E-commerce', 'Branding', 
    'Marketing digital', 'Design UX/UI', 'Développement sur-mesure'
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Contenu du site
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez l'équipe et le portfolio de votre site vitrine
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open('/website', '_blank')}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Voir le site
            </Button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="team" className="gap-2">
                <Users className="w-4 h-4" />
                Équipe
                <Badge variant="secondary" className="ml-1">{teamMembers.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="portfolio" className="gap-2">
                <Briefcase className="w-4 h-4" />
                Portfolio
                <Badge variant="secondary" className="ml-1">{caseStudies.length}</Badge>
              </TabsTrigger>
            </TabsList>

            {/* TEAM TAB */}
            <TabsContent value="team">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-slate-700">Membres de l'équipe</h2>
                <Button onClick={() => openTeamModal()} className="gap-2" style={{ backgroundColor: theme.colors?.accent }}>
                  <Plus className="w-4 h-4" />
                  Ajouter un membre
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : teamMembers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Aucun membre dans l'équipe</p>
                    <Button onClick={() => openTeamModal()} className="mt-4 gap-2">
                      <Plus className="w-4 h-4" />
                      Ajouter le premier membre
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-16"></TableHead>
                        <TableHead>Membre</TableHead>
                        <TableHead>Rôle</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teamMembers.map((member) => (
                        <TableRow key={member.id} className="hover:bg-slate-50">
                          <TableCell>
                            {member.photo_url ? (
                              <img 
                                src={member.photo_url} 
                                alt={member.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                                <Users className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-800">{member.name}</p>
                              {member.bio && (
                                <p className="text-sm text-slate-500 truncate max-w-[200px]">{member.bio}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{member.role}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-slate-500">
                              {member.email && <p>{member.email}</p>}
                              {member.linkedin_url && (
                                <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                  <LinkIcon className="w-3 h-3" />
                                  LinkedIn
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={member.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                              {member.is_active ? 'Visible' : 'Masqué'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleMemberVisibility(member)}>
                                {member.is_active ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openTeamModal(member)}>
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteTeamMember(member.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>

            {/* PORTFOLIO TAB */}
            <TabsContent value="portfolio">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-slate-700">Réalisations / Portfolio</h2>
                <Button onClick={() => openPortfolioModal()} className="gap-2" style={{ backgroundColor: theme.colors?.accent }}>
                  <Plus className="w-4 h-4" />
                  Ajouter une réalisation
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : caseStudies.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Aucune réalisation dans le portfolio</p>
                    <Button onClick={() => openPortfolioModal()} className="mt-4 gap-2">
                      <Plus className="w-4 h-4" />
                      Ajouter la première réalisation
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-20"></TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {caseStudies.map((caseStudy) => (
                        <TableRow key={caseStudy.id} className="hover:bg-slate-50">
                          <TableCell>
                            {caseStudy.image_url ? (
                              <img 
                                src={caseStudy.image_url} 
                                alt={caseStudy.title}
                                className="w-16 h-12 rounded object-cover"
                              />
                            ) : (
                              <div className="w-16 h-12 rounded bg-slate-200 flex items-center justify-center">
                                <Image className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-800">{caseStudy.title}</p>
                              {caseStudy.description && (
                                <p className="text-sm text-slate-500 truncate max-w-[250px]">{caseStudy.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-slate-600">{caseStudy.client}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{caseStudy.category || '-'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={caseStudy.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {caseStudy.published ? 'Publié' : 'Brouillon'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleCasePublished(caseStudy)}>
                                {caseStudy.published ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openPortfolioModal(caseStudy)}>
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCaseStudy(caseStudy.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* TEAM MEMBER MODAL */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              {editingMember ? 'Modifier le membre' : 'Ajouter un membre'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom complet *</Label>
                <Input
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle / Poste *</Label>
                <Input
                  value={memberForm.role}
                  onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                  placeholder="Directeur Artistique"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={memberForm.bio}
                onChange={(e) => setMemberForm({ ...memberForm, bio: e.target.value })}
                placeholder="Courte description du membre..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>URL Photo</Label>
              <Input
                value={memberForm.photo_url}
                onChange={(e) => setMemberForm({ ...memberForm, photo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                  placeholder="jean@agence.com"
                />
              </div>
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input
                  value={memberForm.linkedin_url}
                  onChange={(e) => setMemberForm({ ...memberForm, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveTeamMember} disabled={submitting} style={{ backgroundColor: theme.colors?.accent }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingMember ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CASE STUDY MODAL */}
      <Dialog open={showPortfolioModal} onOpenChange={setShowPortfolioModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              {editingCase ? 'Modifier la réalisation' : 'Ajouter une réalisation'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre du projet *</Label>
                <Input
                  value={caseForm.title}
                  onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                  placeholder="Refonte site e-commerce"
                />
              </div>
              <div className="space-y-2">
                <Label>Client *</Label>
                <Input
                  value={caseForm.client}
                  onChange={(e) => setCaseForm({ ...caseForm, client: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select 
                value={caseForm.category} 
                onValueChange={(v) => setCaseForm({ ...caseForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={caseForm.description}
                onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                placeholder="Description du projet..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Résultats obtenus</Label>
              <Textarea
                value={caseForm.results}
                onChange={(e) => setCaseForm({ ...caseForm, results: e.target.value })}
                placeholder="+50% de conversions, 200% de ROI..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Image principale (URL)</Label>
              <Input
                value={caseForm.image_url}
                onChange={(e) => setCaseForm({ ...caseForm, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Lien du projet</Label>
              <Input
                value={caseForm.link}
                onChange={(e) => setCaseForm({ ...caseForm, link: e.target.value })}
                placeholder="https://www.client.com"
              />
            </div>
            <div className="border-t pt-4">
              <Label className="text-slate-600">Témoignage client (optionnel)</Label>
              <div className="mt-2 space-y-2">
                <Textarea
                  value={caseForm.testimonial}
                  onChange={(e) => setCaseForm({ ...caseForm, testimonial: e.target.value })}
                  placeholder="Ce que le client a dit du projet..."
                  rows={2}
                />
                <Input
                  value={caseForm.testimonial_author}
                  onChange={(e) => setCaseForm({ ...caseForm, testimonial_author: e.target.value })}
                  placeholder="Nom du client - Poste"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPortfolioModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveCaseStudy} disabled={submitting} style={{ backgroundColor: theme.colors?.accent }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingCase ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebsiteContentPage;
